import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowRight, Stethoscope, Building2, ShieldCheck, Anchor, ClipboardCheck,
  Globe2, Zap, BarChart3, CloudUpload, Check, Mail, Phone, HeartPulse,
} from "lucide-react";
import { Reveal, MaskedLines, Stagger, StaggerItem } from "@/components/site/Reveal";

const HERO_IMG = "/assets/7ef07b19b264f741759adacb5570ab0254b534842991e8d83aa101dc57bc7968.png";
const ABOUT_IMG = "https://images.unsplash.com/photo-1758691463198-dc663b8a64e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxtZWRpY2FsJTIwcHJvZmVzc2lvbmFsJTIwY2xpbmljfGVufDB8fHx8MTc4NDA5OTIwNHww&ixlib=rb-4.1.0&q=85";

const pemeFeatures = [
  "Same Day Medical Review", "Independent Medical Opinion", "ILO Guideline Compliance",
  "Client Specific Medical Standards", "Additional Investigation Recommendations",
  "Medical Risk Assessment", "Secure Digital Medical Records", "Cloud Data Backup",
];

const auditFeatures = [
  "Medical Process Audits", "Quality Assurance Reviews", "Clinic Performance Assessment",
  "Compliance Monitoring", "Equipment & Documentation Review",
  "Corrective Action Recommendations", "Annual Compliance Reports",
];

const wellnessGroups = [
  { heading: "Pre-Employment Health Check-up (PEHC)", items: ["Medical Examination", "Fitness Assessment"] },
  { heading: "Annual Health Check-up (AHC)", items: ["Corporate Employees", "Marine Staff"] },
  { heading: "Psychological Assessment", items: ["Behavioural Assessment", "Cognitive Evaluation"] },
  { heading: "Clinical Nutrition & Lifestyle Management", items: ["Weight Management", "Lifestyle Coaching"] },
  { heading: "Corporate Health Analytics", items: ["Health Risk Analysis", "Department-wise Risk Analysis"] },
];

const whyItems = [
  { icon: ShieldCheck, text: "Reduce Medical Risk" },
  { icon: Anchor, text: "Reduce Medical Repatriation" },
  { icon: ClipboardCheck, text: "Ensure ILO Compliance" },
  { icon: Globe2, text: "Standardize Medical Decisions Worldwide" },
  { icon: Zap, text: "Faster Medical Clearance" },
  { icon: BarChart3, text: "Improve Clinic Performance" },
  { icon: CloudUpload, text: "Secure Cloud Storage" },
];

const industries = [
  { name: "Ship Management Companies", img: "/assets/f762632d0256a3f6f95599a44920330fd91a7d6c8ad11e5d5ba1d67b5e3ab7a8.png" },
  { name: "Crewing & Manning Companies", img: "/assets/9722c24a227616d1499fca645529ffeec5b3b025c6a1078327d6230b94090226.png" },
  { name: "Maritime Medical Clinics", img: "/assets/f798a5b9e8b5daccae0ef8ac792817da93fdd97593532eb329ae50b4c1a0295c.png" },
  { name: "Offshore Operators", img: "/assets/9676b95fc6be5b4bf85e9fd91078ed957709e58830136a7a630cc79e555215a1.png" },
  { name: "Cruise Companies", img: "/assets/84fc1f301ba5c8564284a3738d7e8a3611ec01038f707537fa9784f09dd42997.png" },
  { name: "Oil & Gas Marine Operations", img: "/assets/241f9c23e5038ccb2fd6fc20be6af439711600e8328afbd1145cffbc130e6498.png" },
];

const FeatureList = ({ items, testPrefix }) => (
  <ul className="mt-6 divide-y divide-slate-100">
    {items.map((f, i) => (
      <li key={i} data-testid={`${testPrefix}-feature-${i}`} className="flex items-center gap-3 py-2.5 text-sm font-medium text-slate-700">
        <span className="h-5 w-5 rounded-full bg-[#2ab5cf]/15 flex items-center justify-center shrink-0">
          <Check size={12} strokeWidth={3} className="text-[#2ab5cf]" />
        </span>
        {f}
      </li>
    ))}
  </ul>
);

export default function Home() {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 700], [0, 120]);
  const heroScale = useTransform(scrollY, [0, 700], [1, 1.1]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden hero-grain bg-[#12294d]">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <img src={HERO_IMG} alt="Doctor reviewing digital medical records with commercial vessel at sea" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#12294d]/85 via-[#1a3a6b]/60 to-[#1a3a6b]/30" />
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-24 w-full">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.8 }}
            className="overline-label mb-8" data-testid="hero-overline"
          >
            Corporate & Maritime Medical Auditing • Est. Mumbai
          </motion.p>

          <h1 data-testid="hero-heading" className="font-display text-white text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[1.05] max-w-4xl">
            <MaskedLines
              lines={[
                <>Med<span className="text-[#2ab5cf]">Verify</span></>,
                "Services",
              ]}
              startDelay={0.2}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.8 }}
            className="mt-6 text-xl md:text-2xl text-white/90 font-medium max-w-2xl"
            data-testid="hero-subtitle"
          >
            Your Trusted Partner in Medical Quality Assurance
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-12 flex flex-wrap gap-4"
          >
            <button
              onClick={() => {
                const el = document.getElementById("about");
                if (window.lenis && el) window.lenis.scrollTo(el, { offset: -80 });
                else el?.scrollIntoView({ behavior: "smooth" });
              }}
              data-testid="hero-cta-about"
              className="btn-primary rounded-full px-8 py-4 text-sm font-bold"
            >
              About MedVerify
            </button>
            <button
              onClick={() => {
                const el = document.getElementById("services");
                if (window.lenis && el) window.lenis.scrollTo(el, { offset: -80 });
                else el?.scrollIntoView({ behavior: "smooth" });
              }}
              data-testid="hero-cta-services"
              className="btn-ghost rounded-full px-8 py-4 text-sm font-bold inline-flex items-center gap-2"
            >
              Explore Our Services <ArrowRight size={16} />
            </button>
            <a href="mailto:info@medverifys.com" data-testid="hero-cta-contact" className="btn-ghost rounded-full px-8 py-4 text-sm font-bold">
              Get In Touch
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 text-[0.65rem] tracking-[0.3em] uppercase"
        >
          Scroll
        </motion.div>
      </section>

      {/* ABOUT */}
      <section id="about" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <h2 className="font-display text-[#12294d] text-4xl md:text-5xl tracking-tight md:whitespace-nowrap" data-testid="about-heading">
              01 — About Med<span className="text-[#2ab5cf]">Verify</span>
            </h2>
            <p className="mt-5 text-lg md:text-xl text-slate-500 font-medium">
              Independent medical review, built for the maritime industry.
            </p>
            <div className="mt-8 space-y-6 text-slate-600 leading-relaxed">
              <p>
                <Link to="/peme-review" data-testid="about-peme-link" className="font-bold text-[#2ab5cf] hover:text-[#1f96ad] underline underline-offset-4 transition-colors">PEME Services</Link>
                : MedVerify Services delivers PEME reviews such as independent maritime medical audits,
                clinic quality assessments, and compliance solutions to reduce medical risk and strengthen
                global healthcare networks.
              </p>
              <div><Link to="/corporate-wellness" data-testid="about-wellness-link" className="font-bold text-[#2ab5cf] hover:text-[#1f96ad] underline underline-offset-4 transition-colors block mb-2">Corporate Wellness</Link><p>MedVerify Services provides Corporate Wellness and Occupational Health services to:</p><ul className="mt-3 space-y-2 list-disc pl-6 text-slate-600 leading-relaxed">
                <li>Promote organizational ROI</li>
                <li>Enhance employee productivity</li>
                <li>Reduce absenteeism</li>
                <li>Support employee wellbeing</li>
                <li>Offer regulatory compliance best practices</li>
                <li>Enable organizations to make informed strategic decisions regarding their employees</li>
              </ul></div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <img src={ABOUT_IMG} alt="Corporate team overlooking commercial shipping port" className="rounded-lg w-full h-[480px] object-cover shadow-2xl shadow-slate-300/60" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-20 md:py-32 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-display text-[#12294d] text-4xl md:text-5xl tracking-tight" data-testid="services-heading">
              02 — Our Services
            </h2>
            <p className="mt-5 text-lg md:text-xl text-slate-500 font-medium">
              Three pillars of medical assurance
            </p>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-8">
            <Reveal delay={0.05}>
              <div className="card-lift relative overflow-hidden bg-white border border-slate-200 rounded-2xl p-8 md:p-9 h-full flex flex-col" data-testid="service-card-peme">
                <span className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#1a3a6b] to-[#2ab5cf]" />
                <div className="flex items-center gap-4">
                  <span className="h-14 w-14 rounded-xl bg-[#2ab5cf]/12 flex items-center justify-center shrink-0">
                    <Stethoscope size={28} className="text-[#2ab5cf]" />
                  </span>
                  <h3 className="font-display text-[#1a3a6b] text-xl tracking-tight leading-snug">
                    Centralized PEME Medical Review & Verification
                  </h3>
                </div>
                <p className="mt-5 text-sm text-slate-500 leading-relaxed">
                  Independent review of all seafarer medical examinations by experienced maritime doctors.
                </p>
                <FeatureList items={pemeFeatures} testPrefix="peme" />
                <Link to="/peme-review" data-testid="service-peme-link" className="link-arrow mt-auto pt-8 inline-flex items-center gap-2 text-sm font-bold text-[#2ab5cf]">
                  Learn more <ArrowRight size={16} />
                </Link>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="card-lift relative overflow-hidden bg-white border border-slate-200 rounded-2xl p-8 md:p-9 h-full flex flex-col" data-testid="service-card-wellness">
                <span className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#1a3a6b] to-[#2ab5cf]" />
                <div className="flex items-center gap-4">
                  <span className="h-14 w-14 rounded-xl bg-[#2ab5cf]/12 flex items-center justify-center shrink-0">
                    <HeartPulse size={28} className="text-[#2ab5cf]" />
                  </span>
                  <h3 className="font-display text-[#1a3a6b] text-xl tracking-tight leading-snug">
                    Corporate Wellness
                  </h3>
                </div>
                <p className="mt-5 text-sm text-slate-500 leading-relaxed">
                  Occupational health & employee wellness solutions for organizations.
                </p>
                <div className="mt-6 space-y-5">
                  {wellnessGroups.map((g, i) => (
                    <div key={i} data-testid={`wellness-group-${i}`}>
                      <p className="text-sm font-bold text-[#1a3a6b]">{g.heading}</p>
                      <ul className="mt-2 space-y-1.5">
                        {g.items.map((it, j) => (
                          <li key={j} className="flex items-center gap-3 text-sm font-medium text-slate-600">
                            <span className="h-5 w-5 rounded-full bg-[#2ab5cf]/15 flex items-center justify-center shrink-0">
                              <Check size={12} strokeWidth={3} className="text-[#2ab5cf]" />
                            </span>
                            {it}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <Link to="/corporate-wellness" data-testid="service-wellness-link" className="link-arrow mt-auto pt-8 inline-flex items-center gap-2 text-sm font-bold text-[#2ab5cf]">
                  Learn more <ArrowRight size={16} />
                </Link>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="card-lift relative overflow-hidden bg-white border border-slate-200 rounded-2xl p-8 md:p-9 h-full flex flex-col" data-testid="service-card-audit">
                <span className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#1a3a6b] to-[#2ab5cf]" />
                <div className="flex items-center gap-4">
                  <span className="h-14 w-14 rounded-xl bg-[#1a3a6b]/8 flex items-center justify-center shrink-0">
                    <Building2 size={28} className="text-[#1a3a6b]" />
                  </span>
                  <h3 className="font-display text-[#1a3a6b] text-xl tracking-tight leading-snug">
                    Clinic Quality Audit & Compliance Program
                  </h3>
                </div>
                <p className="mt-5 text-sm text-slate-500 leading-relaxed">
                  Improving consistency across your approved clinic network.
                </p>
                <FeatureList items={auditFeatures} testPrefix="audit" />
                <Link to="/clinic-audit" data-testid="service-audit-link" className="link-arrow mt-auto pt-8 inline-flex items-center gap-2 text-sm font-bold text-[#2ab5cf]">
                  Learn more <ArrowRight size={16} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHY MED VERIFY */}
      <section className="py-20 md:py-32 bg-[#12294d] hero-grain relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <Reveal>
            <h2 className="font-display text-white font-bold text-4xl md:text-5xl tracking-tight" data-testid="why-heading">
              03 — Why Med<span className="text-[#2ab5cf]">Verify</span>
            </h2>
            <p className="mt-5 text-lg md:text-xl text-slate-300 font-medium">
              We help clients navigate risk with confidence
            </p>
          </Reveal>
          <Stagger className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyItems.map((item, i) => (
              <StaggerItem key={i}>
                <div className="card-lift bg-white border border-slate-200 rounded-lg p-8 h-full" data-testid={`why-card-${i}`}>
                  <item.icon size={24} className="text-[#1a3a6b]" />
                  <p className="mt-5 text-[#12294d] font-bold">{item.text}</p>
                </div>
              </StaggerItem>
            ))}
            <StaggerItem>
              <Link to="/why-med-verify" data-testid="why-more-link" className="link-arrow flex items-center justify-center gap-2 h-full min-h-[130px] rounded-lg border border-dashed border-[#2ab5cf]/50 text-[#2ab5cf] font-bold text-sm hover:bg-[#2ab5cf]/10 transition-colors">
                Discover why clients choose us <ArrowRight size={16} />
              </Link>
            </StaggerItem>
          </Stagger>
        </div>
      </section>

      {/* INDUSTRIES MARQUEE */}
      <section className="py-20 md:py-28 bg-white overflow-hidden" data-testid="industries-section">
        <Reveal className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-display text-[#12294d] font-bold text-4xl md:text-5xl tracking-tight" data-testid="industries-heading">
            04 — Industries Served
          </h2>
        </Reveal>
        <div className="mt-12">
          <Marquee speed={30} gradient gradientColor="#ffffff" gradientWidth={100} pauseOnHover>
            {industries.map((ind, i) => (
              <div key={i} className="mx-4 w-[320px] md:w-[380px] bg-white border border-slate-200 rounded-xl overflow-hidden shadow-lg shadow-slate-200/60" data-testid={`industry-card-${i}`}>
                <img src={ind.img} alt={ind.name} className="w-full h-52 md:h-60 object-cover" />
                <p className="px-6 py-5 font-display font-bold text-lg md:text-xl text-[#12294d] tracking-tight whitespace-normal">
                  {ind.name}
                </p>
              </div>
            ))}
          </Marquee>
        </div>
      </section>

      {/* CONTACT BAND */}
      <section className="py-20 md:py-28 bg-[#f8fafc]" data-testid="home-contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="bg-[#1a3a6b] rounded-2xl p-10 md:p-14 hero-grain relative overflow-hidden">
              <div className="relative z-10">
                <h2 className="font-display font-bold text-white text-4xl md:text-5xl tracking-tight" data-testid="home-contact-title">
                  Contact
                </h2>
                <div className="mt-6 flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-10">
                  <p className="text-xl md:text-2xl text-white font-semibold whitespace-nowrap">
                    Med<span className="text-[#2ab5cf]">Verify</span> Services
                  </p>
                  <a href="mailto:info@medverifys.com" data-testid="home-contact-email" className="inline-flex items-center gap-3 text-lg md:text-xl text-slate-200 font-medium hover:text-[#2ab5cf] transition-colors whitespace-nowrap">
                    <span className="h-10 w-10 rounded-full bg-[#2ab5cf]/20 flex items-center justify-center shrink-0">
                      <Mail size={18} className="text-[#2ab5cf]" />
                    </span>
                    info@medverifys.com
                  </a>
                  <a href="tel:+919867171984" data-testid="home-contact-phone" className="inline-flex items-center gap-3 text-lg md:text-xl text-slate-200 font-medium hover:text-[#2ab5cf] transition-colors whitespace-nowrap">
                    <span className="h-10 w-10 rounded-full bg-[#2ab5cf]/20 flex items-center justify-center shrink-0">
                      <Phone size={18} className="text-[#2ab5cf]" />
                    </span>
                    +91 98671 71984
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
